import { List, ListItem, ListItemText, Stack, Typography, useTheme } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import ModalV2 from '/@/components/bayer/modal-v2';
import settings from '/@/core/settings';
import { _ } from '/@/locale';

function YieldEstimationModal({ open, setOpen }: { open: boolean; setOpen: (value: boolean) => void }) {
    const theme = useTheme();
    const { i18n } = useTranslation();
    const content = useMemo(() => {
        return [
            {
                title: _('Definir los puntos de muestreo en la superficie del lote'),

                items: [
                    _('Un punto cada 10-15 ha distribuidos homogéneamente.'),
                    _('Si el lote es irregular, aumentar el número de muestras.'),
                    _(
                        'Si hay ambientes contrastantes, (suelo, relieve) dividir en sectores y tomar muestras en cada uno.'
                    ),
                ],
            },
            {
                title: _('Recolectar datos en cada punto'),
                items: [
                    _('Seleccionar un surco al azar.'),
                    _('Contar 21 plantas contiguas y medir la distancia entre la planta 1 y la 21.'),
                    _('Retirar todas las espigas de esas plantas (principales y secundarias).'),
                ],
            },
            {
                title: _('Calcular la cantidad de espigas por hectárea'),
                items: [
                    _('Determinar el número total de espigas y usar la distancia medida para calcular **Espigas/ha.**'),
                ],
            },
            {
                title: _('Analizar las espigas'),
                items: [
                    _('Clasificar las espigas en 3 grupos según tamaño.'),
                    _('Elegir una espiga promedio de cada grupo (7 espigas por grupo).'),
                    _('Si hay espigas secundarias, seleccionar una representativa.'),
                ],
            },
            {
                title: _('Calcular los granos por espiga'),
                items: [
                    _('Contar el número de hileras y granos por hilera en cada espiga seleccionada.'),
                    _('Sacar el promedio sumando los granos de las 3 espigas principales y la secundaria.'),
                    _('Dividir el total por 3 para obtener **Granos/Espiga.**'),
                ],
            },
            {
                title: _('Determinar el peso del grano'),
                items: [
                    _(
                        'Utilizar el valor del catálogo del híbrido o pesar 1.000 granos en balanza para obtener el **P1000.**'
                    ),
                ],
            },
            {
                title: _('Cargar los datos en {{appName}}', { appName: settings.app.name }),
                items: [
                    _(
                        'Una vez tomadas las muestras, el productor debe ingresar los datos en su seguimiento de lote en {{appName}}.',
                        { appName: settings.app.name }
                    ),
                    _('La plataforma calculará el rendimiento estimado, facilitando la toma de decisiones.'),
                ],
            },
        ];
    }, [i18n.resolvedLanguage]);
    return (
        <ModalV2
            open={open}
            onClose={() => setOpen(false)}
            title={_('Guía para estimar el rendimiento')}
            bodyProps={{ alignItems: 'stretch', sx: { textAlign: 'left' } }}
        >
            <Stack spacing={2} px={3}>
                <Typography variant='subtitle1' color='primary' sx={{ fontWeight: 700 }}>
                    {_(
                        'Indicá al productor cómo tomar muestras en el lote y estimar el rendimiento de su cultivo. Luego, recordale cargar los datos en su seguimiento de lote en {{appName}} para obtener el resultado.',
                        { appName: settings.app.name }
                    )}
                </Typography>
                <List sx={{ listStyleType: 'decimal', pl: 2 }}>
                    {content.map((section, index) => (
                        <ListItem
                            key={index}
                            sx={{
                                display: 'list-item',
                                flexDirection: 'column',
                                padding: 0,
                                paddingBottom: 2,
                                typography: 'body2',
                                '&::marker': {
                                    fontWeight: 'bold',
                                    fontSize: theme.typography.body2.fontSize,
                                    color: theme.palette.primary.main,
                                },
                            }}
                        >
                            <Typography variant='body2' color='primary' sx={{ fontWeight: 'bold' }}>
                                {section.title}
                            </Typography>
                            <List sx={{ listStyleType: 'disc', padding: 0 }}>
                                {section.items.map((item, subIndex) => (
                                    <ListItem key={subIndex} sx={{ display: 'list-item', padding: 0 }}>
                                        <ListItemText
                                            primary={
                                                <Typography variant='body2' color='primary'>
                                                    <ReactMarkdown
                                                        components={{
                                                            strong: ({ children }) => (
                                                                <Typography
                                                                    variant='body2'
                                                                    color='primary'
                                                                    component='span'
                                                                    sx={{ fontWeight: 'bold' }}
                                                                >
                                                                    {children}
                                                                </Typography>
                                                            ),
                                                        }}
                                                    >
                                                        {item}
                                                    </ReactMarkdown>
                                                </Typography>
                                            }
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </ListItem>
                    ))}
                </List>
            </Stack>
        </ModalV2>
    );
}

export default YieldEstimationModal;
